// frontend/src/pages/student/LessonContentPage.js

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../modules/api';
import StudentHeader from '../../components/layout/StudentHeader';
import InteractiveDrill from '../../components/content/InteractiveDrill';
import ContentRenderer from '../../components/content/ContentRenderer'; 
// ✅✅✅ کامپوننت جدید شعر تعاملی را ایمپورت کنید
import InteractivePoem from '../../components/content/InteractivePoem'; 
import DiscussionForum from '../../components/content/DiscussionForum';
import { FaArrowLeft, FaVideo, FaFileAlt, FaPencilAlt, FaSpinner, FaBookOpen } from 'react-icons/fa';
import '../../assets/styles/global.css';
import '../../assets/styles/ContentRenderer.css';

// ==============================================================================
// ۱. کامپوننت نمایش دهنده هر بلوک محتوایی (اصلاح شده)
// ==============================================================================
const ContentBlockRenderer = ({ block }) => {
    // تعریف متغیرهای شرطی
    const isTextContent = block.content_type === 'Text';
    const isStandardHtmlContent = block.content_type === 'StandardHtml';
    const isDrillContent = block.content_type === 'Drill';
    const isVideoContent = block.content_type === 'Video';
    // ✅✅✅ نوع جدید را اضافه کنید
    const isInteractivePoem = block.content_type === 'InteractivePoem';
    
    const getIcon = () => {
        switch (block.content_type) {
            case 'Video': return <FaVideo className="ml-2" />;
            case 'InteractivePoem': return <FaBookOpen className="ml-2" />; // آیکون شعر
            case 'Drill':
            case 'Quiz':
            case 'Simulation': return <FaPencilAlt className="ml-2" />;
            default: return <FaFileAlt className="ml-2" />;
        }
    };
    
    return (
        <div className={`content-block ${block.content_type.toLowerCase()}-block`}>
            {/* عنوان هدر فقط برای محتوای غیرشعری نمایش داده شود */}
            {!isInteractivePoem && (
                <h3 className="content-block-header">
                    {getIcon()}
                    {block.title}
                </h3>
            )}
            
            <div className="prose"> 
                
                {/* ۱. رندر HTML استاندارد */}
                {(isStandardHtmlContent || isTextContent) && block.text_content && (
                    <ContentRenderer htmlContent={block.text_content} />
                )}

                {/* ۲. رندر Drill */}
                {isDrillContent && block.json_data && (
                    <InteractiveDrill blockData={block} /> 
                )}

                {/* ۳. رندر Video */}
                {isVideoContent && block.external_link && (
                    <div className="video-wrapper">
                        <p><strong>موضوع:</strong> {block.title}</p>
                        <a href={block.external_link} target="_blank" rel="noopener noreferrer">
                            لینک مشاهده ویدیوی آموزشی (کلیک کنید)
                        </a>
                    </div>
                )}

                {/* ✅✅✅ ۴. رندر شعر تعاملی */}
                {isInteractivePoem && block.json_data && (
                    // داده‌های JSON را به عنوان prop به کامپوننت پاس می‌دهیم
                    <InteractivePoem poemData={block.json_data} />
                )}
            </div>
        </div>
    );
};

// ==============================================================================
// ۲. کامپوننت اصلی صفحه (با راه‌حل StrictMode)
// ==============================================================================
const LessonContentPage = () => {
    const { lessonId } = useParams();
    const navigate = useNavigate();
    const [lessonData, setLessonData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const effectRan = useRef(false);

    // تابع ردیابی پیشرفت
    const trackContentProgress = async (blockId, blockType) => {
        // ✅ شعر تعاملی هم باید VIEWED ثبت شود
        if (blockType !== 'Text' && blockType !== 'Video' && blockType !== 'StandardHtml' && blockType !== 'InteractivePoem') {
            return;
        }
        try {
            await axiosInstance.post(`/core/student/content/track-progress/`, {
                content_block_id: blockId,
                status: 'VIEWED',
            });
        } catch (err) {
            console.warn(`Progress tracking failed/skipped for block ${blockId} (${blockType}):`, err.response?.data?.detail || err.message);
        }
    };

    useEffect(() => {
        // گیت (Gate) برای StrictMode
        if (effectRan.current === true && process.env.NODE_ENV === 'development') {
             return; 
        }
       
        const fetchContent = async () => {
            if (!lessonId) {
                setError("شناسه درس نامعتبر است.");
                setLoading(false);
                return;
            }
            setLoading(true);
            setError(null);
            try {
                const response = await axiosInstance.get(`/core/student/lessons/${lessonId}/content/`);
                setLessonData(response.data);
                
                // حلقه سریالی (برای جلوگیری از قفل شدن DB)
                for (const block of response.data.content_blocks) {
                    // ✅✅✅ نوع جدید شعر را به حلقه اضافه کنید
                    if (block.content_type === 'Text' || block.content_type === 'Video' || block.content_type === 'StandardHtml' || block.content_type === 'InteractivePoem') {
                        await trackContentProgress(block.id, block.content_type);
                    }
                }
                
            } catch (err) {
                console.error("Error fetching lesson content:", err);
                setError(err.response?.data?.detail || "خطا در دریافت محتوای درس.");
            } finally {
                setLoading(false);
            }
        };
        
        fetchContent();
        
        // ثبت کن که اجرا شده
        return () => {
            effectRan.current = true;
        }
        
    }, [lessonId]); 


    const handleGoBack = () => {
        navigate(-1);
    };
    
    // ... (بخش‌های loading و error JSX بدون تغییر) ...
    if (loading) {
        return <div className="text-center p-5 min-h-screen pt-24"><FaSpinner className="spinner text-3xl mx-auto" /></div>;
    }

    if (error) {
        return (
            <div className="app-layout-student">
                 <StudentHeader />
                 <div className="page-content">
                    <div className="alert alert-danger mt-16">{error}</div>
                    <button onClick={handleGoBack} className="button button-secondary mt-4">بازگشت</button>
                 </div>
            </div>
        );
    }
    
    return (
        <div className="app-layout-student">
            <StudentHeader />
            <div className="page-content">
                <button onClick={handleGoBack} className="button button-secondary mb-6 inline-flex items-center">
                    <FaArrowLeft className="ml-2" /> بازگشت به لیست دروس
                </button>
                
                {/* ✅✅✅ اصلاح: کلاس‌های PADDING و SHADOW از اینجا حذف شدند تا کامپوننت شعر تمام عرض باشد */}
                <div className="bg-white rounded-xl border-t-4 border-indigo-600 space-y-8 lesson-render-wrapper">
                    <h1 className="text-3xl font-extrabold text-gray-900 border-b pb-3 mb-6 flex items-center" style={{padding: '0 1.5rem'}}> {/* پدینگ داخلی برای هدر */}
                        <FaBookOpen className="ml-3 text-indigo-600" /> {lessonData?.title || "درس"}
                    </h1>
                    
                    {lessonData?.content_blocks && lessonData.content_blocks.length > 0 ? (
                        lessonData.content_blocks.map((block) => (
                            <ContentBlockRenderer key={block.id} block={block} />
                        ))
                    ) : (
                        <p className="text-center text-gray-500 py-10">هیچ محتوایی برای این درس تعریف نشده است.</p>
                    )}
                </div>
                {/* ✅✅✅ گام ۴.۲: افزودن تالار گفتگو به انتهای صفحه ✅✅✅ */}
                {/* ما lessonId را داریم، پس آن را به کامپوننت پاس می‌دهیم.
                  این کامپوننت خودش لودینگ و دریافت داده‌ها را مدیریت می‌کند.
                */}
                {lessonId && (
                    <div className="mt-8">
                        <DiscussionForum lessonId={lessonId} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default LessonContentPage;